import type { FC } from "react";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";

import type { ContractContactGroup } from "../types";

import { useContractContactsService } from "../services/use-contract-contacts-service";
import { contractContactGroups } from "../utils/contract-contact-row";

import DeleteOccupantDialog from "./delete-occupant-dialog";
import NoContactDialog from "./no-contact-dialog";
import OccupantDrawer from "./occupant-drawer";
import OccupantsTable from "./occupants-table";
import OccupantsTableSkeleton from "./occupants-table-skeleton";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

interface ContactsCardProps {
  contractId: string | number;
  /** Advances the flow — the card's own footer owns the Confirm button. */
  onConfirm: () => void;
}

/**
 * The contract's saved contacts: who gets alerted, at which number or address,
 * on which channels. One card — header, table, confirm — as the alerts step
 * renders it.
 *
 * FD-7784 removed the cap of 3 automatic alerts per contract (TO CONFIRM with
 * product): restoring it means counting the rows of `contacts.data` again.
 */
const ContactsCard: FC<ContactsCardProps> = ({ contractId, onConfirm }) => {
  const { t } = useTranslation(["occupants"]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Which contact the drawer is editing; `undefined` opens it on a blank form.
  const [editingContactId, setEditingContactId] = useState<string | undefined>(undefined);
  const [deleting, setDeleting] = useState<ContractContactGroup | null>(null);
  const [confirmingNoContact, setConfirmingNoContact] = useState(false);

  const contacts = useContractContactsService(contractId);
  const groups = contractContactGroups(contacts.data?.data ?? []);

  const openDrawer = (contactId?: string) => {
    setEditingContactId(contactId);
    setDrawerOpen(true);
  };

  // Moving on with nobody to alert is allowed, but only once the customer says
  // so. Decided off a list that actually loaded: a failed or pending fetch says
  // nothing about whether contacts exist, so it confirms as it always did.
  const confirm = () => {
    if (contacts.isSuccess && groups.length === 0) {
      setConfirmingNoContact(true);

      return;
    }

    onConfirm();
  };

  return (
    <div className="follo-card mt-7">
      <div className="flex items-center justify-between gap-4 px-8 py-4">
        <h3 className="follo-label min-w-0">{t("occupants:occupants-card-title", "Saved contacts")}</h3>

        <Button
          className="shrink-0 cursor-pointer bg-white shadow-xs"
          type="button"
          variant="outline"
          onClick={() => openDrawer()}
        >
          <Plus />
          {t("occupants:occupants-add-contact", "Add a contact")}
        </Button>
      </div>

      <Separator />

      {contacts.isPending ? (
        <OccupantsTableSkeleton />
      ) : contacts.isError ? (
        <p className="px-8 py-10 text-center text-sm text-destructive" role="alert">
          {t("occupants:occupants-load-error", "Failed to load the contacts.")}
        </p>
      ) : (
        <OccupantsTable contractId={contractId} groups={groups} onDelete={setDeleting} onEdit={openDrawer} />
      )}

      <div className="flex justify-end px-8 py-4">
        <Button className="cursor-pointer" type="button" onClick={confirm}>
          {t("occupants:occupants-confirm", "Confirm")}
        </Button>
      </div>

      <OccupantDrawer
        contactId={editingContactId}
        contractId={contractId}
        open={drawerOpen}
        onAddAnother={() => setEditingContactId(undefined)}
        onOpenChange={setDrawerOpen}
      />

      <DeleteOccupantDialog
        contractId={contractId}
        group={deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      />

      <NoContactDialog
        open={confirmingNoContact}
        onConfirm={() => {
          setConfirmingNoContact(false);
          onConfirm();
        }}
        onOpenChange={setConfirmingNoContact}
      />
    </div>
  );
};

export default ContactsCard;
